import { useState } from "react";
import { TESTIDS } from "@/constants/testIds";
import { Lock, ArrowRight } from "lucide-react";

export default function EmailGate({ onSubmit, loading, error }) {
  const [form, setForm] = useState({
    email: "",
    first_name: "",
    last_name: "",
    job_title: "",
    company: "",
  });
  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const canSubmit = form.email.trim() && /.+@.+\..+/.test(form.email);

  return (
    <div
      className="mt-14 glass rounded-2xl p-8 md:p-12 relative overflow-hidden fade-in"
      style={{
        borderColor: "rgba(107,122,85,0.35)",
      }}
      data-testid="email-gate-section"
    >
      <div
        className="absolute -bottom-24 -right-24 w-[500px] h-[500px] rounded-full opacity-50 pointer-events-none"
        style={{
          background: "radial-gradient(circle, rgba(107,122,85,0.16) 0%, transparent 60%)",
          filter: "blur(80px)",
        }}
      />
      <div className="relative grid lg:grid-cols-2 gap-10 items-start">
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Lock className="w-4 h-4" style={{ color: "var(--brand-sage)" }} />
            <div className="label-eyebrow">See what is driving your score</div>
          </div>
          <h2 className="font-serif text-3xl md:text-5xl leading-tight mb-4">
            See what is driving your score.
          </h2>
          <p className="text-[color:var(--text-secondary)] leading-relaxed">
            Enter your business email to save this analysis, unlock the query-level
            evidence, and receive a shareable report that shows:
          </p>
          <ul className="mt-4 space-y-2 text-[color:var(--text-secondary)]">
            <li className="flex items-start gap-2.5">
              <span className="mt-1" style={{ color: "var(--brand-sage)" }}>→</span>
              All the queries we tested
            </li>
            <li className="flex items-start gap-2.5">
              <span className="mt-1" style={{ color: "var(--brand-sage)" }}>→</span>
              Detailed sub-scores
            </li>
            <li className="flex items-start gap-2.5">
              <span className="mt-1" style={{ color: "var(--brand-sage)" }}>→</span>
              Competitor, OTA, and publisher appearances
            </li>
            <li className="flex items-start gap-2.5">
              <span className="mt-1" style={{ color: "var(--brand-sage)" }}>→</span>
              The five highest-priority content opportunities
            </li>
            <li className="flex items-start gap-2.5">
              <span className="mt-1" style={{ color: "var(--brand-sage)" }}>→</span>
              A shareable Local Authority Report
            </li>
          </ul>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit && !loading) onSubmit(form);
          }}
          className="glass rounded-2xl p-6 md:p-8"
        >
          <div className="space-y-4">
            <div>
              <label className="label-eyebrow block mb-1.5">Business email *</label>
              <input
                className="input-base"
                type="email"
                placeholder="you@yourhotel.com"
                value={form.email}
                onChange={setField("email")}
                data-testid={TESTIDS.emailGateEmail}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-eyebrow block mb-1.5">First name</label>
                <input
                  className="input-base"
                  value={form.first_name}
                  onChange={setField("first_name")}
                  data-testid={TESTIDS.emailGateFirstName}
                />
              </div>
              <div>
                <label className="label-eyebrow block mb-1.5">Last name</label>
                <input
                  className="input-base"
                  value={form.last_name}
                  onChange={setField("last_name")}
                  data-testid={TESTIDS.emailGateLastName}
                />
              </div>
            </div>
            <div>
              <label className="label-eyebrow block mb-1.5">Role</label>
              <select
                className="input-base"
                value={form.job_title}
                onChange={setField("job_title")}
                data-testid={TESTIDS.emailGateJobTitle}
                style={{
                  appearance: "auto",
                  cursor: "pointer",
                  color: form.job_title ? "var(--text-primary)" : "var(--text-muted)",
                }}
              >
                <option value="" disabled>Select your role…</option>
                <option value="Property marketing">Property marketing</option>
                <option value="Sales and marketing">Sales and marketing</option>
                <option value="General management">General management</option>
                <option value="Regional or corporate marketing">Regional or corporate marketing</option>
                <option value="Ownership or asset management">Ownership or asset management</option>
                <option value="Agency or consultant">Agency or consultant</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <label className="label-eyebrow block mb-1.5">Company / management group</label>
              <input
                className="input-base"
                value={form.company}
                onChange={setField("company")}
                data-testid={TESTIDS.emailGateCompany}
              />
            </div>
          </div>

          {error && (
            <div className="mt-4 text-sm text-[color:var(--score-low)]" data-testid="email-gate-error">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={!canSubmit || loading}
            className="btn-gold w-full mt-6 justify-center"
            data-testid={TESTIDS.emailGateSubmit}
          >
            {loading ? "Unlocking…" : "Show Me the Full Report"}
            <ArrowRight className="w-4 h-4" />
          </button>

          <div
            className="text-xs mt-3 text-center"
            style={{ color: "var(--text-muted)" }}
            data-testid="email-gate-reassurance"
          >
            No credit card required.
          </div>

          <div className="text-xs text-[color:var(--text-muted)] mt-4 leading-relaxed">
            By submitting, you agree to receive FieldGuide insights. We&apos;ll never share your
            information without permission.
          </div>
        </form>
      </div>
    </div>
  );
}
